import * as cx from 'classnames';
import {
    POST_DELETED_BECAUSE_reason,
    POST_DELETED_BY_ADMIN,
    POST_DELETED_BY_AUTHOR,
    POST_REMOVE_COMPLAINTS,
    TOPIC_MODERATOR,
} from 'mk/autogenerated/translations/Post.19dd01c0a7f2158786a082b7e54e7ddd'
import {
    boldize,
    getInlinedImages,
    linebreaksbr,
    mdLinkize,
    mentionize,
    replaceImageMarks,
    upperCaseFirst,
    urlize,
    vimeoize,
    youtubize,
} from 'mk/bazaar/common/filters';
import { authorIsModerator } from 'mk/bazaar/common/userUtils';
import assetUrl from 'mk/common/assetUrl';
import { photoUrl } from 'mk/photo/photoUrl';
import { user_profile_url } from 'mk/urls/functions';
import ReplyButton from 'mk2/apps/forum/components/ReplyButton';
import { CONTESTS_TOPIC_ID } from 'mk2/apps/forum/constants';
import PostMenu from 'mk2/apps/forum/containers/PostMenu/PostMenu';
import { ContestEntity, PostEntity } from 'mk2/apps/forum/schemas';
import { isCounsellingPost, largestAvailablePhotoVersionOfPost, linkize } from 'mk2/apps/forum/utils';
import AnnouncementBox, { BoxType } from 'mk2/components/AnnouncementBox';
import AvatarImg from 'mk2/components/AvatarImg';
import Emojify from 'mk2/components/Emojify';
import Img from 'mk2/components/Img';
import Complaints from 'mk2/containers/Complaints/Complaints';
import Like, { LikeType } from 'mk2/containers/Like/Like';
import { timeAt } from 'mk2/helpers/timeformat';
import * as React from 'react';
import styles from './Post.mscss';

interface OwnProps {
    post: PostEntity;
    contest: ContestEntity;
    mobile: boolean;
    style?: any;
    className?: string;
    role?: 'author' | 'moderator' | 'admin';

    hasReply: boolean;

    onTopicClickedStoreEvent?();
    onReply(username: string);
}

export default class Post extends React.Component<OwnProps> {

    public shouldComponentUpdate(n: OwnProps) {
        const p = this.props;

        return p.post.id !== n.post.id || p.post.comments !== n.post.comments
            || p.post.likeable.likesCount !== n.post.likeable.likesCount
            || p.post.likeable.isLiked !== n.post.likeable.isLiked
            || p.post.stickerId !== n.post.stickerId
            || p.post.deletedAt !== n.post.deletedAt
            || p.post.sticky !== n.post.sticky
            || (p.contest === null && n.contest !== null); // Contest is loaded
    }

    public render() {
        const {
            post, style, role, hasReply, className, onTopicClickedStoreEvent, contest,
        } = this.props;

        const inlinedImages = getInlinedImages(post.perex);
        const attachedImages = post.photos.filter((photo, idx) => !(idx in inlinedImages));
        const authorIsTopicModerator = authorIsModerator(post.author, post.topic.moderators);
        const internalLinkTestPostIds = [14127, 629834, 27890, 50846, 55997, 69675, 506893, 508921, 585591, 588848, 597328, 707386, 669742, 708543, 712487, 713168, 716218, 717137, 724324, 726746, 733252, 734167, 734882, 736333, 88181, 56748, 747236, 736100]; // Miesal - zoznam ID postov, v ktorych chceme napevno povolit syntax pre prelinkovanie. Skusame vplyv interneho prelinkovania na SEO vysledky.

        // prepare post message body
        let bodyChunks: React.ReactChild[] = [post.perex];
        if (post.perex !== '') {
            if (post.topic.id === CONTESTS_TOPIC_ID) {
                bodyChunks = boldize(vimeoize(youtubize(mdLinkize(bodyChunks))));
            }
            if (internalLinkTestPostIds.indexOf(post.id) !== -1) {
                bodyChunks = linkize(bodyChunks);
            }
            bodyChunks = linebreaksbr(upperCaseFirst(mentionize(urlize(bodyChunks, 50, {nofollow: !post.topic.isCommercial}))));
            bodyChunks = replaceImageMarks(
                bodyChunks, post.photos, styles.Post__body__photos,
                (photo) => largestAvailablePhotoVersionOfPost(photo, 's600x600'),
            );
        }

        const showReply = hasReply && !post.deletedAt && !post.topic.deletedAt;
        const isCounselling = isCounsellingPost(post);
        const showLike = !post.deletedAt && !post.topic.deletedAt && !isCounselling;

        return (
            <div className={cx(styles.Post, className, post.deletedAt && styles['Post--deleted'])} style={style}>
                <div className={styles.Post__body}>
                    <h1
                        className={cx(
                            styles.Post__body__heading,
                            post.perex === '' && styles.Post__body__no_perex,
                            )}
                    >
                        <Emojify>
                            {upperCaseFirst(post.heading)}
                        </Emojify>
                    </h1>

                    <div className={styles.Post__body__container}>
                        <div className={styles.Post__body__meta}>
                            {!isCounselling && (
                                <div className={styles.Post__body__meta__block}>
                                    <a href={user_profile_url(post.author.username)}>
                                        <AvatarImg
                                            size={24}
                                            user={post.author}
                                            className={styles.Post__body__meta__avatar}
                                        />
                                    </a>
                                    <a
                                        href={user_profile_url(post.author.username)}
                                        className={styles.Post__body__meta__author}
                                    >
                                        {post.author.username}
                                    </a>
                                    {authorIsTopicModerator &&
                                            <span className={styles.Post__body__meta__title}>{TOPIC_MODERATOR}</span>
                                        }
                                    <div className={cx(styles.Post__body__meta__created, styles.Post__body__meta__sep)}>
                                        {timeAt(post.created)}
                                    </div>
                                </div>
                            )}
                            {isCounselling &&
                                <div className={styles.Post__body__meta__created}>
                                    {timeAt(post.created)}
                                </div>
                            }
                        </div>

                        <PostMenu
                            className={styles.Post__menu}
                            post={post}
                            contest={contest}
                        />

                        <div className={styles.Post__body__content}>
                            <div className={styles.Post__body__content__text}>
                                <div>
                                    {post.deletedAt && (
                                        <AnnouncementBox
                                            message={post.deletedByAuthor ? POST_DELETED_BY_AUTHOR : POST_DELETED_BY_ADMIN}
                                            deletedBy={post.deletedBy ? post.deletedBy.username : null}
                                            detailMessage={post.deletedReason ? post.deletedReason : null}
                                            deletedAt={post.deletedAt}
                                            hasDetailMessage={!!post.deletedReason}
                                            icon={'fa fa-trash'}
                                            reasonValue={POST_DELETED_BECAUSE_reason}
                                            type={BoxType.Deleted}
                                        />
                                    )}

                                    {post.perex !== '' && (
                                        <div className={styles.Post__body__content__text__perex}>
                                            <Emojify size={17}>
                                                {bodyChunks}
                                            </Emojify>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {post.stickerId && (
                                <div>
                                    <Img
                                        lazy
                                        src={assetUrl + 'img/photoblog/stickers/' + post.stickerId + '.png'}
                                        width={75}
                                        height={75}
                                    />
                                </div>
                            )}

                            {attachedImages.length > 0 && (
                                <div>
                                    {attachedImages.map((photo) => (
                                        <Img
                                            lazy
                                            key={photo.id}
                                            src={photoUrl(photo, largestAvailablePhotoVersionOfPost(photo, 's600x600'))}
                                            height={photo.height}
                                            width={photo.width}
                                            className={styles.Post__body__photos}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {(role === 'admin' || role === 'moderator') && (
                    <Complaints className={styles.Post__complaints} id={post.id} entity="forum-thread" deleteLabel={POST_REMOVE_COMPLAINTS} />
                )}

                {(showReply || showLike) && (
                    <div className={styles.Post__footer}>
                        {showLike && (
                            <Like entity="thread" id={post.id} type={LikeType.IconCount} />
                        )}
                        {showReply && (
                            <ReplyButton onClick={this.handleOnReply} />
                        )}
                    </div>
                )}
            </div>
        );
    }

    private handleOnReply = () => {
        const { post, onReply } = this.props;
        onReply(post.author.username);
    };

}
